import React, { useEffect, useState, useRef } from "react";
import { X, Loader2, Edit3, FileDown } from "lucide-react";
import { TRIM_SIZES } from "./BookSetupModal";

const BLEED = 0.125;
// Was 36 px/in (way too small on modern displays). 90 px/in shows a 6×9
// paperback wrap at ~1000px wide — large enough to actually inspect the
// spine alignment without squinting.
const PREVIEW_PX_PER_INCH = 90;
const DISPLAY_SCALE = 60;

/**
 * AssemblePreviewModal — renders the exact same stitched full-wrap image
 * the PDF exports, then layers click hot-zones over Back, Spine and Front
 * for jump-to-edit. No CSS-overlap math, no gaps.
 */
const AssemblePreviewModal = ({ open, onClose, setup, panelStates, onJumpToPanel, onExportPDF, exporting }) => {
  const [stitchUrl, setStitchUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);
  const [containerW, setContainerW] = useState(1100);

  useEffect(() => {
    const update = () => {
      // Push preview wider — was max 1300, now uses up to 95vw so on a
      // standard laptop the wrap reads at ~1600px instead of ~1100px.
      const w = Math.min(window.innerWidth * 0.95, 1600);
      setContainerW(w);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setStitchUrl(null);

    const fabric = window.fabric;
    if (!fabric) return;

    const trim = TRIM_SIZES.find((t) => t.value === setup.trim_size) || TRIM_SIZES[0];
    const trimW = trim.w;
    const trimH = trim.h;
    const spineW = setup.spine_width_inches || 0.25;

    const renderPanel = (key) => new Promise((resolve) => {
      const dims = key === "spine"
        ? { trimW: spineW, trimH }
        : { trimW, trimH };
      const wPx = (dims.trimW + BLEED * 2) * DISPLAY_SCALE;
      const hPx = (dims.trimH + BLEED * 2) * DISPLAY_SCALE;
      const el = document.createElement("canvas");
      el.width = Math.round(wPx);
      el.height = Math.round(hPx);
      const c = new fabric.StaticCanvas(el, { width: el.width, height: el.height });
      const json = panelStates[key];
      const finish = () => {
        c.getObjects().filter((o) => (o.name || "").startsWith("_guide")).forEach((g) => c.remove(g));
        c.renderAll();
        const dataUrl = c.toDataURL({ format: "png", multiplier: PREVIEW_PX_PER_INCH / DISPLAY_SCALE });
        c.dispose();
        resolve(dataUrl);
      };
      if (!json) {
        c.backgroundColor = "#FFFFFF";
        c.renderAll();
        const dataUrl = c.toDataURL({ format: "png", multiplier: PREVIEW_PX_PER_INCH / DISPLAY_SCALE });
        c.dispose();
        resolve(dataUrl);
        return;
      }
      c.loadFromJSON(json, finish);
    });

    const loadImg = (src) => new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

    (async () => {
      try {
        const [backUrl, spineUrl, frontUrl] = await Promise.all([
          renderPanel("back"), renderPanel("spine"), renderPanel("front"),
        ]);
        const totalWidthInches = trimW * 2 + spineW + BLEED * 2;
        const totalHeightInches = trimH + BLEED * 2;
        const totalW = Math.round(totalWidthInches * PREVIEW_PX_PER_INCH);
        const totalH = Math.round(totalHeightInches * PREVIEW_PX_PER_INCH);
        const stitch = document.createElement("canvas");
        stitch.width = totalW;
        stitch.height = totalH;
        const ctx = stitch.getContext("2d");
        const [backImg, spineImg, frontImg] = await Promise.all([
          loadImg(backUrl), loadImg(spineUrl), loadImg(frontUrl),
        ]);
        // Same overlap math as the PDF export
        ctx.drawImage(backImg, 0, 0);
        ctx.drawImage(spineImg, Math.round(trimW * PREVIEW_PX_PER_INCH), 0);
        ctx.drawImage(frontImg, Math.round((trimW + spineW) * PREVIEW_PX_PER_INCH), 0);
        if (cancelled) return;
        setStitchUrl(stitch.toDataURL("image/png"));
        setLoading(false);
      } catch (err) {
        console.error("preview build failed", err);
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, panelStates, setup.trim_size, setup.spine_width_inches]);

  if (!open) return null;

  const trim = TRIM_SIZES.find((t) => t.value === setup.trim_size) || TRIM_SIZES[0];
  const trimW = trim.w;
  const trimH = trim.h;
  const spineW = setup.spine_width_inches || 0.25;
  const totalWidthInches = trimW * 2 + spineW + BLEED * 2;
  const totalHeightInches = trimH + BLEED * 2;

  // Display scaling: fit total width into containerW
  const fitW = containerW - 64; // padding
  const scale = Math.min(1, fitW / (totalWidthInches * PREVIEW_PX_PER_INCH));
  const displayW = totalWidthInches * PREVIEW_PX_PER_INCH * scale;
  const displayH = totalHeightInches * PREVIEW_PX_PER_INCH * scale;

  // Hot-zone widths (in display px). Bleeds are split 50/50 at internal seams.
  const backDispW = (trimW + BLEED) * PREVIEW_PX_PER_INCH * scale;       // outer left bleed + back trim
  const spineDispW = spineW * PREVIEW_PX_PER_INCH * scale;                // exactly spine trim width
  const frontDispW = displayW - backDispW - spineDispW;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#2C2C2A]/60 backdrop-blur-sm px-4 py-6 overflow-y-auto" data-testid="assemble-modal">
      <div ref={containerRef} className="w-full max-w-[1700px] bg-white rounded-[12px] border border-[#D3D1C7] shadow-2xl my-auto">
        <div className="px-7 py-5 border-b border-[#D3D1C7] flex items-center justify-between">
          <div>
            <h2 className="text-2xl tracking-tight" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }}>
              Full-wrap preview
            </h2>
            <p className="text-sm text-[#5F5E5A] mt-0.5">
              Back · Spine · Front · Total {totalWidthInches.toFixed(3)}″ × {totalHeightInches.toFixed(3)}″
              <span className="ml-2 text-[#5F5E5A]/70">(spine {spineW.toFixed(3)}″)</span>
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-[#F1EFE8] text-[#5F5E5A]" aria-label="Close" data-testid="assemble-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-8 bg-[#E8E5DA] flex items-center justify-center min-h-[420px]">
          {loading && (
            <div className="text-center" data-testid="assemble-loading">
              <Loader2 className="w-6 h-6 animate-spin text-[#534AB7] mx-auto" />
              <p className="mt-3 text-xs text-[#5F5E5A]">Stitching panels…</p>
            </div>
          )}
          {!loading && stitchUrl && (
            <div
              className="relative bg-white shadow-[0_30px_70px_-20px_rgba(0,0,0,0.3)]"
              style={{ width: displayW, height: displayH }}
              data-testid="assemble-preview"
            >
              <img src={stitchUrl} alt="Full-wrap preview" className="block w-full h-full object-fill" />

              {/* Trim-line markers — make the back↔spine and spine↔front
                  seams visible so the user can verify alignment. These
                  are visual aids on the preview only; they do NOT appear
                  in the exported PDF. */}
              <span
                className="pointer-events-none absolute top-0 bottom-0 border-l border-dashed border-[#534AB7]/60"
                style={{ left: backDispW }}
                title="Back ↔ Spine seam"
              />
              <span
                className="pointer-events-none absolute top-0 bottom-0 border-l border-dashed border-[#534AB7]/60"
                style={{ left: backDispW + spineDispW }}
                title="Spine ↔ Front seam"
              />
              {/* Outer trim corners — small ticks at the four print corners */}
              {[
                { left: BLEED * PREVIEW_PX_PER_INCH * scale, top: BLEED * PREVIEW_PX_PER_INCH * scale },
                { left: displayW - BLEED * PREVIEW_PX_PER_INCH * scale, top: BLEED * PREVIEW_PX_PER_INCH * scale },
                { left: BLEED * PREVIEW_PX_PER_INCH * scale, top: displayH - BLEED * PREVIEW_PX_PER_INCH * scale },
                { left: displayW - BLEED * PREVIEW_PX_PER_INCH * scale, top: displayH - BLEED * PREVIEW_PX_PER_INCH * scale },
              ].map((p, i) => (
                <span
                  key={i}
                  className="pointer-events-none absolute w-3 h-3 border-l border-t border-[#D74343]/70"
                  style={{
                    left: p.left, top: p.top,
                    transform: `scale(${i % 2 ? -1 : 1}, ${i < 2 ? 1 : -1})`,
                    transformOrigin: "top left",
                  }}
                />
              ))}

              {/* Hot zones */}
              <button
                onClick={() => onJumpToPanel("back")}
                data-testid="assemble-jump-back"
                title="Edit back cover"
                className="group absolute top-0 left-0 h-full focus:outline-none"
                style={{ width: backDispW }}
              >
                <PanelLabel label="Back" />
              </button>
              <button
                onClick={() => onJumpToPanel("spine")}
                data-testid="assemble-jump-spine"
                title="Edit spine"
                className="group absolute top-0 h-full focus:outline-none"
                style={{ left: backDispW, width: spineDispW }}
              >
                <PanelLabel label="Spine" small />
              </button>
              <button
                onClick={() => onJumpToPanel("front")}
                data-testid="assemble-jump-front"
                title="Edit front cover"
                className="group absolute top-0 h-full focus:outline-none"
                style={{ left: backDispW + spineDispW, width: frontDispW }}
              >
                <PanelLabel label="Front" />
              </button>
            </div>
          )}
        </div>

        <div className="px-7 py-4 border-t border-[#D3D1C7] flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-[#5F5E5A]">
            Click any panel above to jump back and edit. The seams meet exactly as they will on the printed wrap.
          </p>
          <div className="flex gap-3">
            <button onClick={onClose} data-testid="assemble-edit-btn" className="px-5 py-2.5 rounded-[8px] border border-[#D3D1C7] text-sm hover:bg-[#F1EFE8] flex items-center gap-2">
              <Edit3 className="w-4 h-4" /> Go back and edit
            </button>
            <button
              onClick={onExportPDF}
              disabled={exporting}
              data-testid="assemble-export-pdf-btn"
              className="px-6 py-2.5 rounded-[8px] bg-[#534AB7] text-white text-sm font-medium hover:bg-[#3C3489] disabled:opacity-50 flex items-center gap-2"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              {exporting ? "Building PDF…" : "Looks good — export PDF"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PanelLabel = ({ label, small = false }) => (
  <span className={`absolute inset-0 flex items-end justify-center pb-2 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-t from-[#2C2C2A]/40 to-transparent`}>
    <span className={`px-2 py-1 rounded-full bg-white/95 ${small ? "text-[10px]" : "text-xs"} font-medium text-[#534AB7] uppercase tracking-wider whitespace-nowrap`}>
      {label} · click to edit
    </span>
  </span>
);

export default AssemblePreviewModal;
